import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/*
 * Copyright (C) 2025 - present Instructure, Inc.
 *
 * This file is part of Canvas.
 *
 * Canvas is free software: you can redistribute it and/or modify it under
 * the terms of the GNU Affero General Public License as published by the Free
 * Software Foundation, version 3 of the License.
 *
 * Canvas is distributed in the hope that it will be useful, but WITHOUT ANY
 * WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 * A PARTICULAR PURPOSE. See the GNU Affero General Public License for more
 * details.
 *
 * You should have received a copy of the GNU Affero General Public License along
 * with this program. If not, see <http://www.gnu.org/licenses/>.
 */
import { ScreenReaderContent } from '@instructure/ui-a11y-content';
import { Alert } from '@instructure/ui-alerts';
import { Button } from '@instructure/ui-buttons';
import { Flex } from '@instructure/ui-flex';
import { FormField } from '@instructure/ui-form-field';
import { Heading } from '@instructure/ui-heading';
import { Text } from '@instructure/ui-text';
import { TruncateText } from '@instructure/ui-truncate-text';
import { View } from '@instructure/ui-view';
import formatMessage from 'format-message';
import { useRef, useState } from 'react';
import CanvasSelect from '../shared/CanvasSelect';
import { validateCaptionFile } from './utils/validation';
export function ManualCaptionCreator({ languages, onPrimary, onCancel, liveRegion, mountNode, }) {
    const [selectedLanguageId, setSelectedLanguageId] = useState('');
    const [selectedFile, setSelectedFile] = useState(null);
    const [showLanguageError, setShowLanguageError] = useState(false);
    const [fileValidationError, setFileValidationError] = useState('');
    const fileInputRef = useRef(null);
    const handleLanguageChange = (_event, data) => {
        if (data) {
            setSelectedLanguageId(String(data));
            setShowLanguageError(false);
        }
    };
    const handleFileUpload = (event) => {
        const file = event.target.files?.[0];
        if (!file)
            return;
        const validation = validateCaptionFile(file);
        if (!validation.valid) {
            setFileValidationError(validation.error || '');
            setSelectedFile(null);
        }
        else {
            setFileValidationError('');
            setSelectedFile(file);
        }
    };
    const handleUploadClick = () => {
        if (!selectedLanguageId) {
            setShowLanguageError(true);
        }
        if (!selectedFile) {
            setFileValidationError(`Please select a file before uploading.`);
        }
        // Only proceed if both are selected
        if (selectedLanguageId && selectedFile) {
            onPrimary(selectedLanguageId, selectedFile);
        }
    };
    return (_jsxs(Flex, { as: "div", direction: "column", gap: "medium", children: [_jsxs(Flex.Item, { overflowY: "hidden", overflowX: "hidden", children: [_jsx(Heading, { variant: "titleCardMini", children: formatMessage('Add New Caption') }), _jsx(Text, { variant: "contentSmall", children: formatMessage('Upload a subtitle track in either the SRT or WebVTT format.') })] }), _jsxs(FormField, { id: "cc-language-select", layout: "inline", width: "100%", label: formatMessage('Language*'), messages: showLanguageError
                    ? [
                        {
                            type: 'newError',
                            text: formatMessage('Please select a language'),
                        },
                    ]
                    : [], children: [_jsx(CanvasSelect, { label: _jsx(ScreenReaderContent, { children: formatMessage('Select Language') }), placeholder: formatMessage('Select Language'), value: selectedLanguageId, mountNode: mountNode, translatedStrings: {
                            USE_ARROWS: 'Use arrow keys to navigate options.',
                            LIST_COLLAPSED: 'List collapsed.',
                            LIST_EXPANDED: 'List expanded.',
                            OPTION_SELECTED: '{option} selected.',
                        }, onChange: handleLanguageChange, children: languages.map(option => (
                        // @ts-expect-error - CanvasSelect.Option is a JS component without TS definitions
                        _jsx(CanvasSelect.Option, { id: option.id, value: option.id, children: option.label }, option.id))) }), showLanguageError && (_jsx(Alert, { variant: "error", screenReaderOnly: true, isLiveRegionAtomic: true, liveRegion: liveRegion, children: formatMessage('Please select a language') }))] }), _jsxs(FormField, { id: "cc-file-upload", layout: "inline", width: "100%", label: formatMessage('File*'), inputContainerRef: el => el?.style.setProperty('min-width', '0'), messages: fileValidationError
                    ? [
                        {
                            type: 'newError',
                            text: fileValidationError,
                        },
                    ]
                    : [], children: [_jsxs(Flex, { gap: "space8", alignItems: "center", children: [_jsx("input", { ref: fileInputRef, type: "file", accept: ".vtt, .srt", onChange: handleFileUpload, style: { display: 'none' } }), _jsx(Flex.Item, { shouldShrink: false, children: _jsx(Button, { onClick: () => fileInputRef.current?.click(), "aria-label": selectedFile
                                        ? formatMessage('Selected file: {name}', { name: selectedFile.name })
                                        : formatMessage('Choose File'), children: formatMessage('Choose File') }) }), !selectedFile && _jsx(Text, { variant: "contentSmall", children: formatMessage('No file chosen') }), selectedFile && (_jsx(View, { minWidth: 0, children: _jsx(Text, { variant: "contentSmall", children: _jsx(TruncateText, { children: selectedFile.name }) }) }))] }), fileValidationError && (_jsx(Alert, { variant: "error", screenReaderOnly: true, isLiveRegionAtomic: true, liveRegion: liveRegion, children: fileValidationError }))] }), _jsxs(Flex, { gap: "small", children: [_jsx(Button, { color: "secondary", onClick: onCancel, textAlign: "center", width: "auto", children: formatMessage('Cancel') }), _jsx(Button, { color: "primary", onClick: handleUploadClick, textAlign: "center", width: "auto", children: formatMessage('Upload') })] })] }));
}
